<script lang="ts" setup>
import type { SelectedRegionsSectionClick } from '.';
import type { CarouselApi } from '@/components/ui/carousel';
import type Carousel from '@/components/ui/carousel/Carousel.vue';
import type { Region } from '~~/core/entities/Region';

import { Container } from '@/components/Container';
import { CarouselGenericAction } from '@/components/ui/carousel/CarouselGenericAction';
import { CarouselItemSelector } from '@/components/ui/carousel/CarouselItemSelector';
import { PropertyTypeEnum } from '~/core/entities/CRMEnums';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { cleanLocationQueryFromPath } from '~/utils/cleanLocationQueryFromPath';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';

import { SelectedRegionsSectionSkeleton } from '.';
import { useFeaturedRegionTags } from './useFeaturedRegionTags';

type ViewMoreClickedParams = {
	href: string;
	clickCount: number;
	ctaText: string;
};

const props = withDefaults(
	defineProps<{
		regions?: Region[];
		isLoading?: boolean;
	}>(),
	{
		regions: () => [],
		isLoading: false
	}
);

const emit = defineEmits<{
	(e: 'cta-view-more-clicked', params: ViewMoreClickedParams): void;
	(
		e: 'carousel_neighborhood_clicked',
		params: SelectedRegionsSectionClick
	): void;
}>();

// Constants
const SLIDE_DELAY = 2000;
const MAX_CAROUSEL_SELECTORS = 6;
const MACROREGION_TAB_ORDER = [
	'São Paulo',
	'Curitiba',
	'Interior SP',
	'Alphaville SP',
	'Litoral Norte SP',
	'Litoral Sul SP'
] as const;

const MACROREGION_LABEL_ALIASES: Record<string, string> = {
	Interior: 'Interior SP',
	Alphaville: 'Alphaville SP',
	'Litoral Norte': 'Litoral Norte SP',
	'Litoral Sul': 'Litoral Sul SP'
};

// Refs and States
const carouselContainerRef = ref<InstanceType<typeof Carousel> | null>(null);

const selectedMacroregion = ref('');
const hoveredRegionTitle = ref<string | null>(null);

const { currentCarouselItemIndex, scrollableSlidesNumber, scrollTo } =
	useCarouselControl({
		carouselContainerRef,
		cyclic: false
	});

const hasRequestedFeaturedRegions = ref(false);
const {
	data: fetchedFeaturedRegions,
	error: fetchedFeaturedRegionsError,
	status: fetchedFeaturedRegionsStatus,
	execute: fetchFeaturedRegions
} = useAsyncData<Region[]>(
	'home:featured-regions',
	() => $fetch<Region[]>('/api/region'),
	{
		default: () => [],
		immediate: false,
		lazy: true,
		server: false
	}
);

const featuredRegions = computed<Region[]>(() =>
	props.regions.length ? props.regions : fetchedFeaturedRegions.value
);

const hasFeaturedRegionsRequestError = computed(
	() => !props.regions.length && !!fetchedFeaturedRegionsError.value
);

const isLoadingFeaturedRegions = computed(
	() =>
		props.isLoading ||
		(!props.regions.length &&
			!hasFeaturedRegionsRequestError.value &&
			(!hasRequestedFeaturedRegions.value ||
				fetchedFeaturedRegionsStatus.value === 'idle' ||
				fetchedFeaturedRegionsStatus.value === 'pending'))
);

const shouldRenderSection = computed(
	() =>
		!hasFeaturedRegionsRequestError.value &&
		(isLoadingFeaturedRegions.value || featuredRegions.value.length > 0)
);

const activeRegions = computed<Region[]>(() =>
	(selectedMacroregion.value
		? featuredRegions.value.filter(
				region => getRegionMacroregion(region) === selectedMacroregion.value
			)
		: featuredRegions.value
	)
		.slice()
		.sort((regionA, regionB) =>
			regionA.name.localeCompare(regionB.name, 'pt-BR', {
				sensitivity: 'base'
			})
		)
);

const {
	bindCarouselVisibleRegionTagRequests,
	getRegionPropertyTypeLinks,
	scheduleVisibleRegionTagsRequest
} = useFeaturedRegionTags({
	activeRegions,
	currentCarouselItemIndex,
	getCarouselApi: () => carouselContainerRef.value?.carouselApi
});

const macroregionTabs = computed(() => {
	const availableMacroregions = new Set<string>();

	featuredRegions.value.forEach(region => {
		const label = getRegionMacroregion(region);

		if (!label) return;

		availableMacroregions.add(label);
	});

	return MACROREGION_TAB_ORDER.filter(label =>
		availableMacroregions.has(label)
	).map(label => ({ label }));
});

const carouselSelectorIndexes = computed<number[]>(() => {
	const totalSlides = scrollableSlidesNumber.value;

	if (totalSlides <= MAX_CAROUSEL_SELECTORS) {
		return Array.from({ length: totalSlides }, (_, index) => index);
	}

	return Array.from({ length: MAX_CAROUSEL_SELECTORS }, (_, index) =>
		Math.round((index * (totalSlides - 1)) / (MAX_CAROUSEL_SELECTORS - 1))
	);
});

const activeCarouselSelectorIndex = computed(() => {
	const selectorIndexes = carouselSelectorIndexes.value;

	if (!selectorIndexes.length) return -1;

	return selectorIndexes.reduce((closestIndex, targetIndex, index) => {
		const currentDistance = Math.abs(
			targetIndex - currentCarouselItemIndex.value
		);
		const closestDistance = Math.abs(
			selectorIndexes[closestIndex]! - currentCarouselItemIndex.value
		);

		return currentDistance < closestDistance ? index : closestIndex;
	}, 0);
});

const isAtFirstCarouselItem = computed(
	() => currentCarouselItemIndex.value <= 0
);

const isAtLastCarouselItem = computed(() => {
	const lastSlideIndex = scrollableSlidesNumber.value - 1;

	return (
		lastSlideIndex <= 0 || currentCarouselItemIndex.value >= lastSlideIndex
	);
});

watch(
	macroregionTabs,
	tabs => {
		if (tabs.some(tab => tab.label === selectedMacroregion.value)) return;

		selectedMacroregion.value = tabs[0]?.label || '';
	},
	{ immediate: true }
);

function handleCarouselInit(carouselApi: CarouselApi | undefined) {
	scrollableSlidesNumber.value = carouselApi?.scrollSnapList().length ?? 0;
	currentCarouselItemIndex.value = carouselApi?.selectedScrollSnap() ?? 0;

	bindCarouselVisibleRegionTagRequests(carouselApi);
	scheduleVisibleRegionTagsRequest(carouselApi);
}

function getRegionMacroregion(region: Region): string {
	const macroregion = region.macroregion || '';

	return MACROREGION_LABEL_ALIASES[macroregion] || macroregion;
}

function handleCarouselNeighborhoodClicked({
	clickLocation,
	cardText,
	neighborhoodName,
	destinationUrl
}: SelectedRegionsSectionClick) {
	emit('carousel_neighborhood_clicked', {
		clickLocation,
		cardText,
		neighborhoodName,
		destinationUrl
	});
}

function scrollSlides(index: number) {
	const totalSlides = scrollableSlidesNumber.value;
	if (!totalSlides) return;

	const lastSlideIndex = totalSlides - 1;
	const clampedIndex = Math.min(Math.max(index, 0), lastSlideIndex);

	scrollTo(clampedIndex, false);
}

function scrollPreviousSlide() {
	if (isAtFirstCarouselItem.value) return;

	scrollSlides(currentCarouselItemIndex.value - 1);
}

function scrollNextSlide() {
	if (isAtLastCarouselItem.value) return;

	scrollSlides(currentCarouselItemIndex.value + 1);
}

function handleTabClick(tab: { label: string }) {
	selectedMacroregion.value = tab.label;
	scrollSlides(0);
	nextTick(() => scheduleVisibleRegionTagsRequest());
}

function getPropertyTypeUrl(
	region: Region,
	propertyType: PropertyTypeEnum
): string {
	return cleanLocationQueryFromPath(
		PropertyFiltersPresenter.getPathFromFilters({
			propertyTypes: [propertyType],
			regions: [region.name],
			states: [region.state],
			cities: [region.city]
		})
	);
}

function getRegionSearchUrl(region: Region): string {
	return cleanLocationQueryFromPath(
		PropertyFiltersPresenter.getPathFromFilters({
			regions: [region.name],
			states: [region.state],
			cities: [region.city]
		})
	);
}

function isRegionHovered(region: Region): boolean {
	return hoveredRegionTitle.value === region.name;
}

function loadFeaturedRegionsWhenVisible() {
	if (props.regions.length || hasRequestedFeaturedRegions.value) return;

	hasRequestedFeaturedRegions.value = true;
	void fetchFeaturedRegions();
}

function handleRegionFocusOut(event: FocusEvent) {
	const currentTarget = event.currentTarget;
	const nextFocusedTarget = event.relatedTarget;

	if (
		currentTarget instanceof HTMLElement &&
		nextFocusedTarget instanceof Node &&
		currentTarget.contains(nextFocusedTarget)
	) {
		return;
	}

	hoveredRegionTitle.value = null;
}

onMounted(loadFeaturedRegionsWhenVisible);
</script>

<template>
	<section v-if="shouldRenderSection" class="relative w-full bg-white">
		<Container
			class="py-11 pl-5 pr-0 md:py-[60px] md:pl-8 md:pr-0 lg:px-12"
			main-class="flex flex-col gap-6"
		>
			<SelectedRegionsSectionSkeleton v-if="isLoadingFeaturedRegions" />
			<template v-else>
				<header class="flex flex-col items-start">
					<Skeleton
						:is-active="isLoading"
						variant="white"
						:class="isLoading ? 'h-[34px] w-1/2 md:w-[359px]' : ''"
					>
						<h2
							class="font-matter-sq text-[28px] font-normal leading-[1.5] tracking-normal text-primary md:text-[36px]"
						>
							Bairros em destaque
						</h2>
					</Skeleton>
				</header>

				<Carousel
					ref="carouselContainerRef"
					class="relative flex w-full flex-col gap-5 md:gap-8"
					:plugins="[WheelGesturesPlugin()]"
					:opts="{
						align: 'start',
						loop: false,
						slidesToScroll: 1
					}"
					@init-api="handleCarouselInit"
				>
					<div
						class="no-scrollbar flex w-full items-start gap-5 overflow-x-auto border-b border-[#f9f9f9] md:gap-7"
						role="tablist"
						aria-label="Macroregiões"
					>
						<button
							v-for="tab in macroregionTabs"
							:key="tab.label"
							type="button"
							role="tab"
							class="flex min-w-max shrink-0 flex-col items-center gap-2 pb-0 font-sans text-[18px] font-normal leading-[1.5] tracking-normal text-[#4c4c4c] disabled:cursor-default disabled:opacity-100 md:text-[20px]"
							:aria-selected="selectedMacroregion === tab.label"
							@click="handleTabClick(tab)"
						>
							<h4 class="whitespace-nowrap font-sans text-inherit font-inherit">
								{{ tab.label }}
							</h4>
							<span
								class="h-0.5 w-full"
								:class="
									selectedMacroregion === tab.label
										? 'bg-primary'
										: 'bg-transparent'
								"
							/>
						</button>
					</div>

					<CarouselContent
						main-class="h-[400px] lg:h-[460px]"
						class="h-full gap-5"
					>
						<CarouselItem
							v-for="(region, index) in activeRegions"
							:key="region.id || region.slug || region.name"
							class="relative h-full basis-[calc(100%_-_40px)] overflow-hidden md:basis-[46%] lg:basis-[calc((100%_-_20px)_/_2)] xl:basis-[calc((100%_-_40px)_/_3)]"
						>
							<div
								class="neighborhood-card group relative flex size-full items-end overflow-hidden bg-primary"
								@mouseenter="hoveredRegionTitle = region.name"
								@mouseleave="hoveredRegionTitle = null"
								@focusin="hoveredRegionTitle = region.name"
								@focusout="handleRegionFocusOut"
							>
								<NuxtLink
									:to="getRegionSearchUrl(region)"
									class="absolute inset-0 z-20 cursor-pointer"
									:aria-label="`Ver imóveis em ${region.name}`"
									@click="
										handleCarouselNeighborhoodClicked({
											clickLocation: 'neighborhood card',
											neighborhoodName: region.name,
											destinationUrl: getRegionSearchUrl(region)
										})
									"
								/>
								<NuxtLink
									:to="region.path"
									class="pointer-events-auto absolute right-4 top-4 z-40 flex h-10 items-center justify-center gap-2 rounded-[2px] bg-primary px-3 py-2 font-sans text-sm font-medium leading-5 text-white opacity-100 transition-opacity duration-300 ease-out hover:bg-primary/80 lg:pointer-events-none lg:opacity-0 lg:group-hover:pointer-events-auto lg:group-hover:opacity-100"
									:class="
										isRegionHovered(region)
											? 'lg:!pointer-events-auto lg:!opacity-100'
											: ''
									"
									@click="
										handleCarouselNeighborhoodClicked({
											clickLocation: 'neighborhood title',
											neighborhoodName: region.name,
											destinationUrl: region.path
										})
									"
								>
									<IconMaterialSymbolsLightList class="size-5 shrink-0" />
									Conhecer o bairro
								</NuxtLink>
								<NuxtImg
									v-if="region.image"
									:src="region.image"
									:alt="region.name"
									width="467"
									height="460"
									sizes="xs:82vw sm:48vw lg:31vw 2xl:467px"
									densities="x1 x2"
									format="webp"
									class="absolute inset-0 z-0 size-full object-cover transition duration-500 group-hover:scale-105 lg:group-hover:brightness-75"
									:quality="index !== 0 ? 85 : 60"
									loading="lazy"
								/>
								<div
									class="pointer-events-none absolute inset-0 z-10 bg-linear-to-b from-transparent to-black/35 transition-colors duration-200 lg:group-hover:to-black/50"
								/>
								<div
									class="pointer-events-none relative z-30 flex min-w-0 flex-col items-start justify-end gap-2 p-4 text-white lg:p-5"
								>
									<h3 class="flex min-w-0 items-center gap-3">
										<span
											class="truncate font-sans text-[20px] font-medium leading-[1.5] tracking-normal drop-shadow-[0_1px_3px_rgba(0,0,0,0.15)] md:text-[24px]"
										>
											{{ region.name }}
										</span>
										<IconMaterialSymbolsNorthEast
											class="size-5 shrink-0 drop-shadow-[0_1px_1.5px_rgba(0,0,0,0.15)]"
										/>
									</h3>
									<div
										class="pointer-events-auto flex max-h-32 flex-wrap gap-1 overflow-hidden opacity-100 transition-[max-height,opacity,transform] duration-300 ease-out lg:pointer-events-none lg:max-h-0 lg:translate-y-2 lg:opacity-0 lg:group-hover:pointer-events-auto lg:group-hover:max-h-32 lg:group-hover:translate-y-0 lg:group-hover:opacity-100"
										:class="
											isRegionHovered(region)
												? 'lg:!pointer-events-auto lg:!max-h-32 lg:!translate-y-0 lg:!opacity-100'
												: ''
										"
									>
										<NuxtLink
											v-for="propertyTypeLink in getRegionPropertyTypeLinks(
												region
											)"
											:key="`${region.name}-${propertyTypeLink.propertyType}`"
											:to="
												getPropertyTypeUrl(
													region,
													propertyTypeLink.propertyType
												)
											"
											class="flex h-10 items-center justify-center rounded-[2px] bg-primary/40 px-3 py-2 font-sans text-sm font-medium leading-5 text-white transition-colors hover:bg-primary/60"
											@click="
												handleCarouselNeighborhoodClicked({
													clickLocation: 'card clicked',
													cardText: propertyTypeLink.label,
													neighborhoodName: region.name,
													destinationUrl: getPropertyTypeUrl(
														region,
														propertyTypeLink.propertyType
													)
												})
											"
										>
											{{ propertyTypeLink.label }}
										</NuxtLink>
									</div>
								</div>
							</div>
						</CarouselItem>
					</CarouselContent>

					<div class="flex w-full items-center gap-5">
						<div class="flex flex-1 justify-end">
							<div
								class="flex w-full max-w-[335px] flex-wrap justify-end gap-2 md:max-w-[420px] md:gap-3"
							>
								<CarouselItemSelector
									v-for="(
										targetIndex, selectorIndex
									) in carouselSelectorIndexes"
									:key="`selector-${targetIndex}`"
									:active="activeCarouselSelectorIndex === selectorIndex"
									:delay="SLIDE_DELAY"
									variant="dark"
									size="sm"
									@click="scrollSlides(targetIndex)"
								/>
							</div>
						</div>
						<div class="flex shrink-0 gap-1">
							<CarouselGenericAction
								variant="dark"
								class="disabled:pointer-events-none disabled:opacity-40"
								aria-label="Ver bairro anterior"
								:disabled="isAtFirstCarouselItem"
								@click="scrollPreviousSlide"
							>
								<IconMaterialSymbolsLightChevronLeft
									width="28px"
									height="28px"
								/>
							</CarouselGenericAction>
							<CarouselGenericAction
								variant="dark"
								class="disabled:pointer-events-none disabled:opacity-40"
								aria-label="Ver próximo bairro"
								:disabled="isAtLastCarouselItem"
								@click="scrollNextSlide"
							>
								<IconMaterialSymbolsLightChevronRight
									width="28px"
									height="28px"
								/>
							</CarouselGenericAction>
						</div>
					</div>
				</Carousel>
			</template>
		</Container>
	</section>
</template>
